import { useSelector } from '@xstate/react';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMount } from 'react-use';

import { TooltipLabel } from '@eluve/blocks';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  HStack,
  Icon,
  Input,
  NewButton,
  P,
  Paragraph,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Text,
  Textarea,
  VStack,
  cn,
} from '@eluve/components';
import { EluveEditor } from '@eluve/eluve-editor';
import { BlockType } from '@eluve/llm-outputs';
import { getTiptapMarkdownContentFromEditor } from '@eluve/markdown-utils';
import { areStringsEqualWithoutSpecialCharacters } from '@eluve/utils';

import { BlockDetailsFormData } from './TreeItem';
import { templateBuilderStore } from './templateBuilderStore';
import { BuilderBlock } from './utilities';

export interface BlockDetailsFormProps {
  block: BuilderBlock;
  onSubmit: () => void;
}

const BaseBlockForm: React.FC<{
  isReadonly: boolean;
  isImported?: boolean;
  onSubmit: () => void;
  id: string;
  type: BlockType;
}> = ({ isReadonly, isImported, onSubmit, id, type }) => {
  const { control, watch } = useFormContext();
  const isAiEnabled = watch('isAiEnabled');

  return (
    <VStack gap={6}>
      {type !== 'group' && (
        <FormField
          control={control}
          name="isAiEnabled"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Fill out this block with</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(value === 'AI');
                  onSubmit();
                }}
                value={field.value ? 'AI' : 'Manual'}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue>{field.value ? 'AI' : 'Manual'}</SelectValue>
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="w-full">
                  <SelectItem
                    className="focus:bg-bg-accent-quaternary"
                    value={'AI'}
                  >
                    <div className="py-1">
                      <Text size="md">AI</Text>
                      <Paragraph size="md" color="tertiary">
                        AI will fill out this block. User can edit the AI draft.
                      </Paragraph>
                    </div>
                  </SelectItem>
                  <SelectItem
                    className="focus:bg-bg-accent-quaternary"
                    value={'Manual'}
                  >
                    <div className="py-1">
                      <Text size="md">Manual</Text>
                      <Paragraph size="md" color="tertiary">
                        This block will start with the placeholder text. User
                        fills it in.
                      </Paragraph>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <VStack gap={3}>
        <FormField
          control={control}
          name="isRequired"
          render={({ field }) => (
            <FormItem>
              <HStack align="start" gap={4}>
                <Switch
                  disabled={!isAiEnabled || isReadonly || isImported}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    onSubmit();
                  }}
                  checked={field.value}
                  id={`${id}-isRequired`}
                />
                <VStack>
                  <FormLabel
                    className={cn(
                      'mt-1',
                      !isAiEnabled && ' text-text-disabled',
                    )}
                    htmlFor={`${id}-isRequired`}
                  >
                    Required
                  </FormLabel>
                  <FormDescription>
                    AI will always try to fill out this block. It should never
                    leave it blank.
                  </FormDescription>
                </VStack>
              </HStack>
              <FormMessage />
            </FormItem>
          )}
        />
        {type === 'text' && (
          <FormField
            control={control}
            name="isEmailEnabled"
            render={({ field }) => (
              <FormItem>
                <HStack align="start" gap={4}>
                  <Switch
                    disabled={isReadonly}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      onSubmit();
                    }}
                    checked={field.value}
                    id={`${id}-isEmailEnabled`}
                  />
                  <VStack>
                    <FormLabel
                      className="mt-1"
                      htmlFor={`${id}-isEmailEnabled`}
                    >
                      Allow users to email this block
                    </FormLabel>
                    <FormDescription>
                      If enabled, users will be given controls to send the
                      contents of this block by email.
                    </FormDescription>
                  </VStack>
                </HStack>
              </FormItem>
            )}
          />
        )}
      </VStack>

      {isAiEnabled ? (
        <FormField
          control={control}
          name="description"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>AI Instructions</FormLabel>
              <FormControl>
                <Textarea
                  className="min-h-40"
                  placeholder={
                    'Describe what the AI should generate in this block. For example, "Write a short description of the patient\'s chief complaint. Do not use full sentences."'
                  }
                  {...field}
                  disabled={isReadonly}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
    </VStack>
  );
};

const CheckboxBlockForm: React.FC<{
  isReadonly: boolean;
  onSubmit: () => void;
}> = ({ isReadonly, onSubmit }) => {
  const { control, setValue, watch } = useFormContext<BlockDetailsFormData>();

  const checkboxOptions = watch('checkboxOptions') as string[];

  // When the length of the checkbox option changes, trigger
  // onSubmit which will try to save the blocks or set form
  // as invalid. Actual value changes are handled with 'onChange'
  useEffect(() => {
    onSubmit();
  }, [checkboxOptions.length, onSubmit]);

  return (
    <VStack>
      <FormItem className="w-full">
        <FormLabel>Checkboxes</FormLabel>
        <VStack gap={2}>
          {checkboxOptions.map((_, index) => (
            <FormField
              key={`checkboxOptions.${index}`}
              name={`checkboxOptions.${index}`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <VStack gap={1}>
                  <FormControl>
                    <HStack wFull>
                      <Input
                        {...field}
                        placeholder={`Option ${index + 1}`}
                        disabled={isReadonly}
                        className={cn(
                          'flex-1',
                          error ? 'border-border-negative-strong' : undefined,
                        )}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                      />
                      {!isReadonly && (
                        <NewButton
                          icon={{ name: 'Trash' }}
                          type="outline"
                          disabled={checkboxOptions.length === 1}
                          onClick={() => {
                            const updatedOptions = [...checkboxOptions];
                            updatedOptions.splice(index, 1);
                            setValue('checkboxOptions', updatedOptions, {
                              shouldValidate: true,
                            });
                          }}
                        />
                      )}
                    </HStack>
                  </FormControl>
                  <FormMessage />
                </VStack>
              )}
            />
          ))}
        </VStack>
      </FormItem>
      <NewButton
        text="Add Option"
        type="outline"
        icon={{
          name: 'Plus',
        }}
        onClick={() => {
          setValue(`checkboxOptions.${checkboxOptions.length}`, '', {
            shouldValidate: true,
          });
        }}
        disabled={Boolean(isReadonly)}
      />
    </VStack>
  );
};

const TextBoxBlockForm: React.FC<{
  isReadonly: boolean;
  onSubmit: () => void;
}> = ({ isReadonly }) => {
  const { control } = useFormContext();
  return (
    <FormField
      control={control}
      name="placeholderText"
      render={({ field }) => (
        <FormItem>
          <HStack>
            <TooltipLabel label="When used in Manual Charting mode, this text will be pre-filled in the text box.">
              <FormLabel className="mt-2 whitespace-nowrap">
                Placeholder Text
                <Icon
                  className="-mt-px ml-1 inline-block"
                  size="xs"
                  name="Info"
                />
              </FormLabel>
            </TooltipLabel>
          </HStack>
          <FormControl>
            <EluveEditor
              disabled={isReadonly}
              content={field.value}
              className="w-full rounded-lg border"
              onUpdate={(editor) => {
                const content = getTiptapMarkdownContentFromEditor(editor);

                if (
                  areStringsEqualWithoutSpecialCharacters(content, field.value)
                ) {
                  return;
                }
                field.onChange(content);
              }}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
};

export const BlockDetailsForm: React.FC<BlockDetailsFormProps> = ({
  onSubmit,
  block,
}) => {
  const isReadonly = useSelector(
    templateBuilderStore,
    (store) => store.context.isReadOnly,
  );
  const isImported = useSelector(
    templateBuilderStore,
    (store) => store.context.isImported,
  );

  const { trigger, watch } = useFormContext<BlockDetailsFormData>();

  const isAiEnabled = watch('isAiEnabled');

  useMount(() => {
    trigger();
  });

  return (
    <VStack align="stretch" gap={4}>
      <BaseBlockForm
        isReadonly={isReadonly}
        isImported={isImported}
        onSubmit={onSubmit}
        id={block.id}
        type={block.type}
      />
      {!isAiEnabled && block.type === 'text' && (
        <TextBoxBlockForm
          isReadonly={isReadonly || Boolean(isImported)}
          onSubmit={onSubmit}
        />
      )}
      {block.type === 'checkbox' && (
        <CheckboxBlockForm
          isReadonly={isReadonly || Boolean(isImported)}
          onSubmit={onSubmit}
        />
      )}
      {block.importedDescription && (
        <HStack>
          <P>Imported Description</P>
          <TooltipLabel label="This description was imported from the EHR. The imported description will be used as a hint to AI when generating a note. Override it by providing your own description above to be used for AI.">
            <Icon size="xs" name="CircleHelp" />
          </TooltipLabel>
          <Textarea disabled={true} value={block.importedDescription} />
        </HStack>
      )}
    </VStack>
  );
};
